import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RotateCcw, Sparkles } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { StoreBadges } from "@/components/landing/StoreBadges";
import { Reveal } from "@/components/landing/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { metaTrack } from "@/lib/metaPixel";
import { gaEvent } from "@/lib/googleAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const track = (campaign) => axios.post(`${API}/track/visit`, { source: "website", medium: "quiz", campaign, path: "/farbtyp-test" }).catch(() => {});

// scoring: w = warm/cool axis, l = light/deep axis
const QUESTIONS = [
  { id: "veins", opts: [{ w: 1 }, { w: -1 }, {}] },
  { id: "jewelry", opts: [{ w: 1 }, { w: -1 }, {}] },
  { id: "sun", opts: [{ w: 1 }, { w: -1 }, {}] },
  { id: "hair", opts: [{ w: 1, l: 1 }, { w: 1, l: -1 }, { w: -1, l: 1 }, { w: -1, l: -1 }, {}] },
  { id: "contrast", opts: [{ l: 1 }, { l: -1 }, {}] },
  { id: "intensity", opts: [{ l: 1 }, { l: -1 }, {}] },
];

const PALETTES = {
  spring: { colors: ["#F4C95D", "#FF8C69", "#8FBC8F", "#F5DEB3", "#45B8AC", "#D2A26E"] },
  autumn: { colors: ["#9C5221", "#7D7A2C", "#C19A6B", "#A93226", "#CF9B2A", "#EFE6D5"] },
  summer: { colors: ["#A9BFD8", "#C9B6D4", "#BC8F8F", "#708090", "#D9D9E8", "#F0EFEA"] },
  winter: { colors: ["#1F2A5C", "#C8102E", "#FFFFFF", "#101014", "#C2185B", "#2E5EAA"] },
};

const I18N = {
  de: {
    metaTitle: "Farbtyp-Test: Welche Farben stehen mir? In 60 Sekunden | Mira Mirror",
    metaDesc: "Kostenloser Farbtyp-Mini-Test: 6 Fragen, ehrliches Ergebnis mit Farbpalette. Keine Anmeldung. Die präzise Analyse mit Foto macht die Mira-App.",
    kicker: "Kostenloser Mini-Test",
    h1: "Welche Farben stehen dir?",
    sub: "6 Fragen, 60 Sekunden, ehrliche Tendenz – ganz ohne Anmeldung.",
    start: "Test starten",
    qOf: (i, n) => `Frage ${i} von ${n}`,
    honest: "Ehrlich gesagt: 6 Fragen ergeben eine Tendenz, keine Analyse. Dein echter Farbtyp hängt von Hautunterton, Licht und Nuancen ab – das prüft die Mira-App mit deinem Foto.",
    resultKicker: "Deine Tendenz",
    paletteTitle: "Diese Farbwelt dürfte dir stehen",
    avoidTitle: "Eher mit Vorsicht",
    ctaTitle: "Willst du es genau wissen?",
    ctaText: "Die Mira-App analysiert dein Foto, bestimmt deinen Farbtyp präzise und prüft jedes Teil deiner Garderobe dagegen – ehrlich, auch wenn’s mal nicht passt.",
    again: "Test wiederholen",
    questions: {
      veins: { q: "Schau auf die Venen an deinem Handgelenk – welche Farbe haben sie?", a: ["Eher grünlich", "Eher bläulich-violett", "Schwer zu sagen / gemischt"] },
      jewelry: { q: "Welcher Schmuck schmeichelt deiner Haut mehr?", a: ["Gold", "Silber", "Beides gleich"] },
      sun: { q: "Wie reagiert deine Haut auf Sonne?", a: ["Wird schnell braun", "Wird schnell rot", "Erst rot, dann braun"] },
      hair: { q: "Deine Naturhaarfarbe kommt am ehesten hin als …", a: ["Goldblond / honigbraun", "Kupfer / warmes Dunkelbraun", "Aschblond / hellbraun kühl", "Dunkelbraun / schwarz", "Mittelbraun neutral"] },
      contrast: { q: "Haar, Brauen und Haut im Spiegel: eher sanfter Übergang oder klarer Kontrast?", a: ["Sanfter Übergang", "Klarer Kontrast", "Dazwischen"] },
      intensity: { q: "Womit bekommst du eher Komplimente?", a: ["Zarte, helle Töne", "Satte, kräftige Farben", "Kommt drauf an"] },
    },
    results: {
      spring: { name: "Warmer, heller Typ", season: "Tendenz Frühling", text: "Warme, klare und helle Farben dürften dein Gesicht zum Leuchten bringen – denk an Creme statt Reinweiss und Koralle statt Pink.", avoid: "Schwarz in Gesichtsnähe, kühle Pastelltöne" },
      autumn: { name: "Warmer, tiefer Typ", season: "Tendenz Herbst", text: "Erdige, satte und warme Töne stehen dir vermutlich am besten – Camel, Rost und Oliv statt klarer Knallfarben.", avoid: "Reinweiss, Schwarz, kühles Neonpink" },
      summer: { name: "Kühler, heller Typ", season: "Tendenz Sommer", text: "Weiche, kühle und gedeckte Farben dürften harmonieren – Taubenblau, Altrosa und Lavendel statt Orange.", avoid: "Orange, warmes Erdbraun, Senfgelb" },
      winter: { name: "Kühler, tiefer Typ", season: "Tendenz Winter", text: "Klare, kühle und kontrastreiche Farben stehen dir vermutlich – Marine, klares Rot und Reinweiss statt Beige.", avoid: "Beige, Camel, gedeckte Erdtöne" },
    },
    teaserKicker: "60-Sekunden-Test",
    teaserTitle: "Welche Farben stehen dir? Finde deine Tendenz",
    teaserCta: "Zum Farbtyp-Test",
  },
  en: {
    metaTitle: "Colour Type Test: Which Colours Suit Me? In 60 Seconds | Mira Mirror",
    metaDesc: "Free colour-type mini test: 6 questions, honest result with a colour palette. No sign-up. The precise photo analysis is done by the Mira app.",
    kicker: "Free mini test",
    h1: "Which colours suit you?",
    sub: "6 questions, 60 seconds, an honest tendency – no sign-up needed.",
    start: "Start the test",
    qOf: (i, n) => `Question ${i} of ${n}`,
    honest: "Honestly: 6 questions give you a tendency, not an analysis. Your true colour type depends on skin undertone, light and nuances – the Mira app checks that with your photo.",
    resultKicker: "Your tendency",
    paletteTitle: "This colour world should suit you",
    avoidTitle: "Handle with care",
    ctaTitle: "Want to know for sure?",
    ctaText: "The Mira app analyses your photo, determines your colour type precisely and checks every piece in your wardrobe against it – honestly, even when it doesn't fit.",
    again: "Retake the test",
    questions: {
      veins: { q: "Look at the veins on your wrist – what colour are they?", a: ["Rather greenish", "Rather bluish-purple", "Hard to tell / mixed"] },
      jewelry: { q: "Which jewellery flatters your skin more?", a: ["Gold", "Silver", "Both equally"] },
      sun: { q: "How does your skin react to sun?", a: ["Tans quickly", "Burns quickly", "Burns first, then tans"] },
      hair: { q: "Your natural hair colour is closest to …", a: ["Golden blonde / honey brown", "Copper / warm dark brown", "Ash blonde / cool light brown", "Dark brown / black", "Neutral mid-brown"] },
      contrast: { q: "Hair, brows and skin in the mirror: soft transition or clear contrast?", a: ["Soft transition", "Clear contrast", "In between"] },
      intensity: { q: "What earns you more compliments?", a: ["Delicate, light tones", "Rich, bold colours", "It depends"] },
    },
    results: {
      spring: { name: "Warm, light type", season: "Spring tendency", text: "Warm, clear and light colours should make your face glow – think cream instead of pure white and coral instead of pink.", avoid: "Black near the face, cool pastels" },
      autumn: { name: "Warm, deep type", season: "Autumn tendency", text: "Earthy, rich and warm tones probably suit you best – camel, rust and olive instead of clear brights.", avoid: "Pure white, black, cool neon pink" },
      summer: { name: "Cool, light type", season: "Summer tendency", text: "Soft, cool and muted colours should harmonise – dove blue, dusty rose and lavender instead of orange.", avoid: "Orange, warm earthy brown, mustard" },
      winter: { name: "Cool, deep type", season: "Winter tendency", text: "Clear, cool and high-contrast colours likely suit you – navy, true red and pure white instead of beige.", avoid: "Beige, camel, muted earth tones" },
    },
    teaserKicker: "60-second test",
    teaserTitle: "Which colours suit you? Find your tendency",
    teaserCta: "Take the colour test",
  },
  es: {
    metaTitle: "Test de colorimetría: ¿qué colores me favorecen? En 60 segundos | Mira Mirror",
    metaDesc: "Mini test de colorimetría gratis: 6 preguntas, resultado honesto con paleta de colores. Sin registro. El análisis preciso con foto lo hace la app de Mira.",
    kicker: "Mini test gratuito",
    h1: "¿Qué colores te favorecen?",
    sub: "6 preguntas, 60 segundos, una tendencia honesta – sin registro.",
    start: "Empezar el test",
    qOf: (i, n) => `Pregunta ${i} de ${n}`,
    honest: "Siendo honestos: 6 preguntas dan una tendencia, no un análisis. Tu colorimetría real depende del subtono de piel, la luz y los matices – eso lo comprueba la app de Mira con tu foto.",
    resultKicker: "Tu tendencia",
    paletteTitle: "Este mundo de color debería favorecerte",
    avoidTitle: "Con precaución",
    ctaTitle: "¿Quieres saberlo con precisión?",
    ctaText: "La app de Mira analiza tu foto, determina tu colorimetría con precisión y la contrasta con cada prenda de tu armario – con honestidad, incluso cuando algo no te queda.",
    again: "Repetir el test",
    questions: {
      veins: { q: "Mira las venas de tu muñeca: ¿de qué color son?", a: ["Más bien verdosas", "Más bien azuladas-violetas", "Difícil de decir / mixtas"] },
      jewelry: { q: "¿Qué joyas favorecen más a tu piel?", a: ["Oro", "Plata", "Ambas por igual"] },
      sun: { q: "¿Cómo reacciona tu piel al sol?", a: ["Se broncea rápido", "Se enrojece rápido", "Primero rojo, luego moreno"] },
      hair: { q: "Tu color de pelo natural se parece más a …", a: ["Rubio dorado / castaño miel", "Cobrizo / castaño oscuro cálido", "Rubio ceniza / castaño claro frío", "Castaño oscuro / negro", "Castaño medio neutro"] },
      contrast: { q: "Pelo, cejas y piel en el espejo: ¿transición suave o contraste claro?", a: ["Transición suave", "Contraste claro", "Entre medias"] },
      intensity: { q: "¿Con qué recibes más cumplidos?", a: ["Tonos delicados y claros", "Colores intensos y vivos", "Depende"] },
    },
    results: {
      spring: { name: "Tipo cálido y claro", season: "Tendencia primavera", text: "Los colores cálidos, claros y luminosos deberían iluminar tu rostro: crema en vez de blanco puro y coral en vez de rosa.", avoid: "Negro cerca del rostro, pasteles fríos" },
      autumn: { name: "Tipo cálido y profundo", season: "Tendencia otoño", text: "Los tonos tierra, intensos y cálidos probablemente te favorecen más: camel, óxido y oliva en vez de colores vivos puros.", avoid: "Blanco puro, negro, rosa neón frío" },
      summer: { name: "Tipo frío y claro", season: "Tendencia verano", text: "Los colores suaves, fríos y empolvados deberían armonizar: azul paloma, rosa empolvado y lavanda en vez de naranja.", avoid: "Naranja, marrón tierra cálido, mostaza" },
      winter: { name: "Tipo frío y profundo", season: "Tendencia invierno", text: "Los colores claros, fríos y contrastados probablemente te favorecen: marino, rojo puro y blanco puro en vez de beige.", avoid: "Beige, camel, tonos tierra apagados" },
    },
    teaserKicker: "Test de 60 segundos",
    teaserTitle: "¿Qué colores te favorecen? Descubre tu tendencia",
    teaserCta: "Hacer el test de color",
  },
  it: {
    metaTitle: "Test armocromia: quali colori mi stanno bene? In 60 secondi | Mira Mirror",
    metaDesc: "Mini test di armocromia gratuito: 6 domande, risultato onesto con palette di colori. Senza registrazione. L'analisi precisa con foto la fa l'app Mira.",
    kicker: "Mini test gratuito",
    h1: "Quali colori ti stanno bene?",
    sub: "6 domande, 60 secondi, una tendenza onesta – senza registrazione.",
    start: "Inizia il test",
    qOf: (i, n) => `Domanda ${i} di ${n}`,
    honest: "Onestamente: 6 domande danno una tendenza, non un'analisi. La tua vera armocromia dipende da sottotono, luce e sfumature – l'app Mira la verifica con la tua foto.",
    resultKicker: "La tua tendenza",
    paletteTitle: "Questo mondo di colori dovrebbe valorizzarti",
    avoidTitle: "Con cautela",
    ctaTitle: "Vuoi saperlo con precisione?",
    ctaText: "L'app Mira analizza la tua foto, determina con precisione la tua armocromia e la confronta con ogni capo del tuo guardaroba – con onestà, anche quando qualcosa non ti dona.",
    again: "Ripeti il test",
    questions: {
      veins: { q: "Guarda le vene sul polso: di che colore sono?", a: ["Piuttosto verdastre", "Piuttosto bluastre-viola", "Difficile dirlo / miste"] },
      jewelry: { q: "Quali gioielli valorizzano di più la tua pelle?", a: ["Oro", "Argento", "Entrambi allo stesso modo"] },
      sun: { q: "Come reagisce la tua pelle al sole?", a: ["Si abbronza in fretta", "Si arrossa in fretta", "Prima rossa, poi abbronzata"] },
      hair: { q: "Il tuo colore di capelli naturale è più vicino a …", a: ["Biondo dorato / castano miele", "Ramato / castano scuro caldo", "Biondo cenere / castano chiaro freddo", "Castano scuro / nero", "Castano medio neutro"] },
      contrast: { q: "Capelli, sopracciglia e pelle allo specchio: passaggio morbido o contrasto netto?", a: ["Passaggio morbido", "Contrasto netto", "Una via di mezzo"] },
      intensity: { q: "Con cosa ricevi più complimenti?", a: ["Toni delicati e chiari", "Colori pieni e decisi", "Dipende"] },
    },
    results: {
      spring: { name: "Tipo caldo e chiaro", season: "Tendenza primavera", text: "Colori caldi, limpidi e luminosi dovrebbero illuminare il tuo viso: crema invece del bianco puro e corallo invece del rosa.", avoid: "Nero vicino al viso, pastelli freddi" },
      autumn: { name: "Tipo caldo e profondo", season: "Tendenza autunno", text: "Toni terrosi, pieni e caldi probabilmente ti donano di più: cammello, ruggine e oliva invece dei colori squillanti.", avoid: "Bianco puro, nero, rosa neon freddo" },
      summer: { name: "Tipo freddo e chiaro", season: "Tendenza estate", text: "Colori morbidi, freddi e polverosi dovrebbero armonizzare: azzurro polvere, rosa antico e lavanda invece dell'arancione.", avoid: "Arancione, marrone terra caldo, senape" },
      winter: { name: "Tipo freddo e profondo", season: "Tendenza inverno", text: "Colori netti, freddi e contrastati probabilmente ti donano: blu navy, rosso puro e bianco ottico invece del beige.", avoid: "Beige, cammello, toni terra spenti" },
    },
    teaserKicker: "Test di 60 secondi",
    teaserTitle: "Quali colori ti stanno bene? Scopri la tua tendenza",
    teaserCta: "Fai il test dei colori",
  },
  fr: {
    metaTitle: "Test de colorimétrie : quelles couleurs me vont ? En 60 secondes | Mira Mirror",
    metaDesc: "Mini test de colorimétrie gratuit : 6 questions, résultat honnête avec palette de couleurs. Sans inscription. L'analyse précise par photo, c'est l'app Mira.",
    kicker: "Mini test gratuit",
    h1: "Quelles couleurs te vont ?",
    sub: "6 questions, 60 secondes, une tendance honnête – sans inscription.",
    start: "Commencer le test",
    qOf: (i, n) => `Question ${i} sur ${n}`,
    honest: "Honnêtement : 6 questions donnent une tendance, pas une analyse. Ta vraie colorimétrie dépend du sous-ton de peau, de la lumière et des nuances – l'app Mira le vérifie avec ta photo.",
    resultKicker: "Ta tendance",
    paletteTitle: "Cet univers de couleurs devrait te mettre en valeur",
    avoidTitle: "À manier avec précaution",
    ctaTitle: "Tu veux le savoir précisément ?",
    ctaText: "L'app Mira analyse ta photo, détermine précisément ta colorimétrie et la confronte à chaque pièce de ta garde-robe – honnêtement, même quand ça ne va pas.",
    again: "Refaire le test",
    questions: {
      veins: { q: "Regarde les veines de ton poignet : de quelle couleur sont-elles ?", a: ["Plutôt verdâtres", "Plutôt bleutées-violettes", "Difficile à dire / mélangées"] },
      jewelry: { q: "Quels bijoux flattent le plus ta peau ?", a: ["Or", "Argent", "Les deux pareil"] },
      sun: { q: "Comment ta peau réagit-elle au soleil ?", a: ["Bronze vite", "Rougit vite", "Rougit d'abord, puis bronze"] },
      hair: { q: "Ta couleur de cheveux naturelle se rapproche le plus de …", a: ["Blond doré / châtain miel", "Cuivré / brun foncé chaud", "Blond cendré / châtain clair froid", "Brun foncé / noir", "Châtain moyen neutre"] },
      contrast: { q: "Cheveux, sourcils et peau dans le miroir : transition douce ou contraste net ?", a: ["Transition douce", "Contraste net", "Entre les deux"] },
      intensity: { q: "Avec quoi reçois-tu le plus de compliments ?", a: ["Tons délicats et clairs", "Couleurs riches et soutenues", "Ça dépend"] },
    },
    results: {
      spring: { name: "Type chaud et clair", season: "Tendance printemps", text: "Les couleurs chaudes, claires et lumineuses devraient illuminer ton visage : crème plutôt que blanc pur et corail plutôt que rose.", avoid: "Noir près du visage, pastels froids" },
      autumn: { name: "Type chaud et profond", season: "Tendance automne", text: "Les tons terreux, riches et chauds te vont probablement le mieux : camel, rouille et olive plutôt que des couleurs vives pures.", avoid: "Blanc pur, noir, rose néon froid" },
      summer: { name: "Type froid et clair", season: "Tendance été", text: "Les couleurs douces, froides et poudrées devraient s'harmoniser : bleu colombe, vieux rose et lavande plutôt qu'orange.", avoid: "Orange, brun terre chaud, moutarde" },
      winter: { name: "Type froid et profond", season: "Tendance hiver", text: "Les couleurs franches, froides et contrastées te vont probablement : marine, rouge pur et blanc pur plutôt que beige.", avoid: "Beige, camel, tons terre éteints" },
    },
    teaserKicker: "Test de 60 secondes",
    teaserTitle: "Quelles couleurs te vont ? Découvre ta tendance",
    teaserCta: "Faire le test couleurs",
  },
};

const scoreResult = (answers) => {
  let w = 0, l = 0;
  QUESTIONS.forEach((q, i) => {
    const s = q.opts[answers[i]] || {};
    w += s.w || 0;
    l += s.l || 0;
  });
  if (w >= 0) return l >= 0 ? "spring" : "autumn";
  return l >= 0 ? "summer" : "winter";
};

export const ColorTestTeaser = () => {
  const { lang } = useLang();
  const c = I18N[lang] || I18N.de;
  return (
    <section className="px-5 sm:px-8" data-testid="colortest-teaser">
      <div className="mx-auto max-w-6xl">
        <Link to="/farbtyp-test" data-testid="colortest-teaser-link" className="group mt-0 flex flex-col gap-4 rounded-2xl border px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8" style={{ borderColor: "var(--line)", background: "var(--surface-alt, var(--surface))" }}>
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ background: "var(--camel)", color: "#fff" }}><Sparkles size={20} /></span>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em]" style={{ color: "var(--camel)" }}>{c.teaserKicker}</p>
              <p className="font-serif-display text-lg sm:text-xl" style={{ color: "var(--on-surface)" }}>{c.teaserTitle}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--camel)" }}>{c.teaserCta} <ArrowRight size={16} /></span>
        </Link>
      </div>
    </section>
  );
};

export default function ColorTypeTest() {
  const { lang } = useLang();
  const c = I18N[lang] || I18N.de;
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    document.title = c.metaTitle;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", c.metaDesc);
    window.scrollTo(0, 0);
  }, [c]);

  const answer = (idx) => {
    const next = [...answers];
    next[step] = idx;
    setAnswers(next);
    if (step + 1 >= QUESTIONS.length) {
      setStep(QUESTIONS.length);
      track("farbtyp_test");
      metaTrack("FarbtestComplete", true);
      gaEvent("farbtest_complete");
    } else {
      setStep(step + 1);
    }
  };

  const done = step >= QUESTIONS.length;
  const resKey = done ? scoreResult(answers) : null;
  const res = resKey ? c.results[resKey] : null;

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }}>
      <Navbar />
      <section className="px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
        <div className="mx-auto max-w-2xl">
          {step === -1 && (
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: "var(--camel)" }} data-testid="colortest-kicker">{c.kicker}</p>
              <h1 className="font-serif-display mt-3 text-4xl sm:text-5xl lg:text-6xl" data-testid="colortest-title">{c.h1}</h1>
              <p className="mt-4 text-base sm:text-lg" style={{ color: "var(--muted-warm)" }}>{c.sub}</p>
              <div className="mt-6 flex gap-2">
                {PALETTES.autumn.colors.map((col) => (
                  <span key={col} className="h-8 w-8 rounded-full border" style={{ background: col, borderColor: "var(--line)" }} />
                ))}
              </div>
              <button data-testid="colortest-start-btn" onClick={() => setStep(0)} className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5" style={{ background: "var(--camel)" }}>
                {c.start} <ArrowRight size={16} />
              </button>
            </Reveal>
          )}

          {step >= 0 && !done && (
            <div data-testid={`colortest-question-${step}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: "var(--camel)" }}>{c.qOf(step + 1, QUESTIONS.length)}</p>
                <div className="flex gap-1.5">
                  {QUESTIONS.map((_, i) => (
                    <span key={i} className="h-1.5 w-6 rounded-full" style={{ background: i <= step ? "var(--camel)" : "var(--line)" }} />
                  ))}
                </div>
              </div>
              <h2 className="font-serif-display mt-5 text-2xl sm:text-3xl">{c.questions[QUESTIONS[step].id].q}</h2>
              <div className="mt-7 flex flex-col gap-3">
                {c.questions[QUESTIONS[step].id].a.map((label, i) => (
                  <button key={i} data-testid={`colortest-answer-${i}`} onClick={() => answer(i)} className="rounded-xl border px-5 py-4 text-left text-base transition-colors duration-200 hover:border-[var(--camel)]" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {done && res && (
            <div data-testid="colortest-result">
              <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: "var(--camel)" }}>{c.resultKicker}</p>
              <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl" data-testid="colortest-result-name">{res.name}</h2>
              <p className="mt-1 text-sm font-medium" style={{ color: "var(--camel)" }}>{res.season}</p>
              <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{res.text}</p>
              <p className="mt-6 text-sm font-medium uppercase tracking-wide">{c.paletteTitle}</p>
              <div className="mt-3 flex gap-2.5">
                {PALETTES[resKey].colors.map((col) => (
                  <span key={col} data-testid="colortest-swatch" className="h-11 w-11 rounded-full border shadow-sm" style={{ background: col, borderColor: "var(--line)" }} />
                ))}
              </div>
              <p className="mt-5 text-sm" style={{ color: "var(--muted-warm)" }}><span className="font-medium" style={{ color: "var(--on-surface)" }}>{c.avoidTitle}:</span> {res.avoid}</p>
              <div className="mt-8 rounded-2xl border p-6" style={{ borderColor: "var(--line)", background: "var(--surface-alt, #FAF6EF)" }}>
                <p className="text-sm italic leading-relaxed" style={{ color: "var(--muted-warm)" }} data-testid="colortest-honest-note">{c.honest}</p>
                <p className="font-serif-display mt-5 text-xl">{c.ctaTitle}</p>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{c.ctaText}</p>
                <div className="mt-5" onClickCapture={() => { track("farbtyp_test_app"); metaTrack("AppBadgeClick", true); gaEvent("app_badge_click"); }} data-testid="colortest-app-cta">
                  <StoreBadges height={44} />
                </div>
              </div>
              <button data-testid="colortest-again-btn" onClick={() => { setStep(-1); setAnswers([]); }} className="mt-6 inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--camel)" }}>
                <RotateCcw size={14} /> {c.again}
              </button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
